import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Chip, Checkbox, FormControlLabel, CircularProgress, Alert, TextField,
  Autocomplete, Divider, Switch, Grid
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import api from '../../services/api';

const BulkClassAllocationDialog = ({ open, onClose, onSuccess }) => {
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [classStreams, setClassStreams] = useState([]);
  const [terms, setTerms] = useState([]);

  const [teacher, setTeacher] = useState(null);
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [existingAllocations, setExistingAllocations] = useState([]);
  const [settings, setSettings] = useState({
    academic_year: new Date().getFullYear(),
    term_id: '',
    start_date: '',
    end_date: '',
    is_primary: true,
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    Promise.all([
      api.get('/report-card/teachers/active'),
      api.get('/academic/all-classes'),
      api.get('/academic/class-streams-filter'),
      api.get('/academic/terms'),
    ]).then(([t, c, cs, tr]) => {
      setTeachers(Array.isArray(t.data) ? t.data : []);
      setClasses(Array.isArray(c.data) ? c.data : []);
      setClassStreams(Array.isArray(cs.data) ? cs.data : cs.data?.data || []);
      const termsData = Array.isArray(tr.data?.data) ? tr.data.data : Array.isArray(tr.data) ? tr.data : [];
      setTerms(termsData);
    }).catch(() => {});
  }, [open]);

  // Fetch ALL existing primary allocations for the year/term (not just this teacher)
  useEffect(() => {
    const params = { academic_year: settings.academic_year, is_active: 1, per_page: 500 };
    if (settings.term_id) params.term_id = settings.term_id;
    api.get('/report-card/class-teacher-allocations', { params })
      .then(res => setExistingAllocations(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => {});
  }, [settings.academic_year, settings.term_id]);

  // Build target options: for each class, if it has streams → one entry per stream; else → class-only entry
  const targetOptions = useMemo(() => {
    const opts = [];
    classes.forEach(cls => {
      const streams = classStreams.filter(cs => cs.class_id === cls.id);
      if (streams.length === 0) {
        const assigned = existingAllocations.some(a => a.class_id === cls.id && !a.stream_id);
        if (!assigned) opts.push({ key: `${cls.id}-null`, class_id: cls.id, stream_id: null, label: cls.name || cls.class_name });
      } else {
        streams.forEach(cs => {
          const assigned = existingAllocations.some(a => a.class_id === cls.id && a.stream_id === cs.stream_id);
          if (!assigned) opts.push({ key: `${cls.id}-${cs.stream_id}`, class_id: cls.id, stream_id: cs.stream_id, label: `${cls.name || cls.class_name} — ${cs.stream_name || cs.stream_id}` });
        });
      }
    });
    return opts;
  }, [classes, classStreams, existingAllocations]);

  // Clear stale selections when available options change
  useEffect(() => {
    setSelectedTargets(prev => prev.filter(t => targetOptions.some(o => o.key === t.key)));
  }, [targetOptions]);

  const allAssigned = classes.length > 0 && targetOptions.length === 0 && existingAllocations.length > 0;

  const handleSubmit = async () => {
    if (!teacher) { setError('Select a teacher'); return; }
    if (selectedTargets.length === 0) { setError('Select at least one class/stream'); return; }
    if (!settings.start_date) { setError('Start date is required'); return; }
    setError('');
    setLoading(true);
    let created = 0, failed = 0;
    for (const t of selectedTargets) {
      try {
        await api.post('/report-card/class-teacher-allocations', {
          teacher_id: teacher.id,
          class_id: t.class_id,
          stream_id: t.stream_id || null,
          academic_year: settings.academic_year,
          term_id: settings.term_id || null,
          start_date: settings.start_date,
          end_date: settings.end_date || null,
          is_primary: settings.is_primary,
          is_active: settings.is_active,
        });
        created++;
      } catch { failed++; }
    }
    setLoading(false);
    onSuccess(`${created} allocation(s) created${failed > 0 ? `, ${failed} failed/skipped` : ''}`, created > 0 ? 'success' : 'error');
    if (created > 0) handleClose();
  };

  const handleClose = () => {
    setTeacher(null);
    setSelectedTargets([]);
    setSettings({ academic_year: new Date().getFullYear(), term_id: '', start_date: '', end_date: '', is_primary: true, is_active: true });
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk Class Teacher Allocation</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {allAssigned && (
            <Alert severity="info" sx={{ mb: 2 }}>
              All classes and streams already have a class teacher assigned for this year/term.
            </Alert>
          )}

          <Autocomplete
            fullWidth
            options={teachers}
            value={teacher}
            onChange={(_, v) => setTeacher(v)}
            getOptionLabel={(o) => `${o.full_name || `${o.last_name} ${o.first_name}`} (${o.staff_number})`}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => <TextField {...params} margin="normal" label="Teacher" required />}
          />

          <Autocomplete
            multiple
            fullWidth
            options={targetOptions}
            value={selectedTargets}
            onChange={(_, v) => setSelectedTargets(v)}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(o, v) => o.key === v.key}
            renderInput={(params) => <TextField {...params} margin="normal" label="Classes / Streams" required />}
            renderTags={(value, getTagProps) =>
              value.map((o, i) => <Chip key={o.key} label={o.label} size="small" {...getTagProps({ index: i })} />)
            }
          />

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>Settings</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="Academic Year" value={settings.academic_year}
                onChange={(e) => setSettings(s => ({ ...s, academic_year: e.target.value }))}
                inputProps={{ min: 2020, max: new Date().getFullYear() }} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                fullWidth
                options={terms}
                value={terms.find(t => t.id === settings.term_id) || null}
                onChange={(_, v) => setSettings(s => ({ ...s, term_id: v?.id || '', start_date: v?.start_date || s.start_date, end_date: v?.end_date || s.end_date }))}
                getOptionLabel={(o) => o.term_name || o.name || ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => <TextField {...params} label="Term (optional)" />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker label="Start Date" value={settings.start_date ? dayjs(settings.start_date) : null}
                  onChange={(v) => setSettings(s => ({ ...s, start_date: v ? v.format('YYYY-MM-DD') : '' }))}
                  slotProps={{ textField: { fullWidth: true, required: true } }} />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker label="End Date (optional)" value={settings.end_date ? dayjs(settings.end_date) : null}
                  onChange={(v) => setSettings(s => ({ ...s, end_date: v ? v.format('YYYY-MM-DD') : '' }))}
                  slotProps={{ textField: { fullWidth: true } }} />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel control={<Switch checked={settings.is_primary} onChange={(e) => setSettings(s => ({ ...s, is_primary: e.target.checked }))} />} label="Primary Class Teacher" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel control={<Switch checked={settings.is_active} onChange={(e) => setSettings(s => ({ ...s, is_active: e.target.checked }))} />} label="Active" />
            </Grid>
          </Grid>

          {selectedTargets.length > 0 && teacher && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <strong>{selectedTargets.length} allocation(s)</strong> will be created for {teacher.full_name || `${teacher.last_name} ${teacher.first_name}`}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading || !teacher || !selectedTargets.length || allAssigned}
          startIcon={loading ? <CircularProgress size={20} /> : null}>
          {loading ? 'Creating...' : `Create ${selectedTargets.length} Allocation(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkClassAllocationDialog;
